import React, { useContext, useEffect } from "react";
import { ThemerPack } from "nemuru-components";
import { ConfigContext } from "../../../contexts/ConfigContext";
import { customThemes, neutralTheme } from "../../../config/themes";
import { AuthContext } from "../../../contexts/AuthContext";
import { getMode } from "../../../utils/getMode";

export default function ThemeManager(props) {
  const {
    state: { appTheme, themed },
    dispatch: configDispatch,
  } = useContext(ConfigContext);
  const {
    state: { agent },
  } = useContext(AuthContext);

  useEffect(() => {
    checkTheme();
  }, [agent]);

  const showZendeskCustomerSupport = (customTheme, customRegistry) => {
    if (!customTheme || !customRegistry) return false;

    return customThemes[customTheme]?.zendeskCustomerSupport;
  };

  const checkTheme = () => {
    if (themed || !agent) return;
    if (themed) return;

    let custom_theme = getThemeFromUrl();
    let custom_registry = agent?.custom_registry;

    const customization = getCustomizationFromUrl();

    if (agent && !custom_theme && !customization) {
      custom_theme = agent.custom_theme;
    }

    if (agent && !custom_registry) {
      custom_registry = agent.custom_registry;
    }

    if (custom_theme && customThemes[custom_theme]) {
      const dataLayer = window.dataLayer || [];
      dataLayer.push({
        event: "zendesk_theme",
        themeName: customThemes[custom_theme]?.themeName,
        color: {
          theme: customThemes[custom_theme]?.palette?.secondary?.main,
          launcher:
            customThemes[custom_theme]?.palette?.secondary?.contrastText,
          launcherText: customThemes[custom_theme]?.palette?.secondary?.main,
          button: customThemes[custom_theme]?.palette?.secondary?.main,
          resultLists: customThemes[custom_theme]?.palette?.secondary?.main,
          header: customThemes[custom_theme]?.palette?.secondary?.main,
          articleLinks: customThemes[custom_theme]?.palette?.secondary?.main,
        },
      });
      dataLayer.push({
        event: "customerSupport",
        customerSupport:
          getMode() === "embedded"
            ? false
            : showZendeskCustomerSupport(custom_theme, custom_registry),
      });
      configDispatch({
        type: "CHANGE_THEME",
        payload: customThemes[custom_theme],
      });
    } else {
      let theme = neutralTheme;
      if (customization) {
        theme = mergeCustomization(customization, theme);
      }
      configDispatch({
        type: "CHANGE_THEME",
        payload: theme,
      });
    }
    configDispatch({
      type: "UPDATE_CONFIG_CONTEXT",
      payload: {
        themed: true,
      },
    });
  };

  return <ThemerPack theme={appTheme}>{props.children}</ThemerPack>;
}

const getThemeFromUrl = () => {
  const url = new URL(window.location.href);
  const theme = url.searchParams.get("theme");
  return theme;
};

const getCustomizationFromUrl = () => {
  const url = new URL(window.location.href);
  const customization = url.searchParams.get("customization");
  if (!customization) return null;
  const decodedCustomization = JSON.parse(atob(customization));
  return {
    shape: decodedCustomization.shape,
    colorBrand: decodedCustomization.colorBrand,
    colorText: decodedCustomization.colorText,
    logoUrl: decodedCustomization.logoUrl,
  };
};

const shapeToCustomBorderRadius = (shape) => {
  if (shape === "rounded") {
    return {
      button: 8,
      paper: 8,
      chip: 6,
    };
  }
  if (shape === "pill") {
    return {
      button: 25,
      paper: 15,
      chip: 6,
    };
  }
  return {
    button: 2,
    paper: 0,
    chip: 0,
  };
};

const mergeCustomization = (customization, theme) => {
  let customBorderRadius = theme.customBorderRadius;
  if (customization.shape) {
    customBorderRadius = shapeToCustomBorderRadius(customization.shape);
  }
  return {
    ...theme,
    logoUrl: customization.logoUrl || theme.logoUrl,
    palette: {
      ...theme.palette,
      primary: {
        ...theme.palette?.primary,
        ...(customization.colorBrand && {
          main: customization.colorBrand,
          contrastText: getOppositeColor(customization.colorBrand),
        }),
      },
      ...(theme.palette?.text && {
        text: {
          ...theme.palette.text,
          ...(customization.colorText && {
            primary: customization.colorText,
          }),
        },
      }),
    },
    customBorderRadius,
  };
};

const getOppositeColor = (hexColor) => {
  // Convert hex color to RGB
  let r = parseInt(hexColor.substr(1, 2), 16);
  let g = parseInt(hexColor.substr(3, 2), 16);
  let b = parseInt(hexColor.substr(5, 2), 16);

  // Calculate luminance
  let luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Decide which color to use based on luminance
  return luminance > 0.5 ? "#000000" : "#FFFFFF";
};
